<script setup>
// @ts-check

import { useI18n } from 'vue-i18n'

const props = defineProps({
  title: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean
  },
  disabled: {
    type: Boolean
  },
  actionDisabled: {
    type: Boolean
  },
  persistent: {
    type: Boolean
  },
  fullscreen: {
    type: Boolean
  },
  hidePrimaryAction: {
    type: Boolean
  },
  hideCancel: {
    type: Boolean
  },
  noActions: {
    type: Boolean
  },
  stackedActions: {
    type: Boolean
  },
  closeOnCancelDisabled: {
    type: Boolean
  }
})

const emit = defineEmits(['action', 'close', 'update:model-value', 'cancel'])

defineOptions({
  name: 'CardDialog'
})

const { t } = useI18n()

const onAction = () => emit('action', { close: () => emit('close') })

const onUpdate = (/** @type {boolean} */ value) => {
  emit('update:model-value', value)
  if (!value) {
    emit('close')
  }
}

const onCancel = () => {
  emit('cancel')

  if (!props.closeOnCancelDisabled) {
    emit('close')
  }
}
</script>

<template>
  <VDialog
    :title
    :persistent
    :fullscreen
    max-width="560"
    model-value
    @update:model-value="onUpdate"
  >
    <VCard>
      <!-- Top bar with close icon -->
      <VToolbar density="compact" class="bg-transparent">
        <VSpacer />
        <TextButton
          v-if="!persistent"
          class="m-0!"
          color="secondary-900"
          icon="$close"
          :aria-label="t('general.close')"
          @click.stop="emit('close')"
        />
      </VToolbar>

      <section
        class="mx-auto flex w-full flex-col justify-center gap-content px-content xs:px-12"
        :class="{
          'h-full': fullscreen
        }"
      >
        <!-- Main title -->
        <slot name="header">
          <header v-if="title" class="flex flex-row items-center gap-content">
            <slot name="prepend-title" />
            <h2 class="text-title font-medium">
              {{ title }}
            </h2>
            <slot name="append-title" />
          </header>
        </slot>

        <!-- Dialog content -->
        <VCardText class="flex flex-col gap-content p-0">
          <slot />
          <slot name="after-content" />
        </VCardText>

        <!-- Dialog bottom actions -->

        <VCardActions
          v-if="!noActions"
          class="mb-12 flex flex-col p-0"
          :class="{
            'md:flex-row-reverse': !stackedActions
          }"
        >
          <slot name="actions" :action="onAction" :close="() => emit('close')">
            <PrimaryButton
              v-if="!hidePrimaryAction"
              class="w-full"
              :class="{
                'md:flex-1': !stackedActions
              }"
              :loading
              :disabled="disabled || actionDisabled"
              @click.stop="onAction"
            >
              <slot name="action">
                {{ t('general.submit') }}
              </slot>
            </PrimaryButton>

            <SecondaryButton
              v-if="!hideCancel && !persistent"
              class="w-full"
              :class="{
                'md:flex-1': !stackedActions,
                'border-transparent': stackedActions
              }"
              :disabled="disabled || loading"
              @click.stop="onCancel"
            >
              <slot name="cancel">
                {{ t('general.cancel') }}
              </slot>
            </SecondaryButton>
          </slot>
        </VCardActions>
      </section>
    </VCard>
  </VDialog>
</template>
